import * as React from "react";
import * as ReactDOM from "react-dom";
import {
    Button,
    CandyTooltip,
    Checkbox,
    ComboBox,
    Gapped,
    Hint,
    Input,
    Link,
    MenuItem,
    MenuSeparator,
    Modal,
    Radio,
    Toast,
} from "react-ui";
import { Unsubscribe } from "redux";
import {
    Employee,
    EmployeeList,
    GetEmployeesWithActivePeriodQuery,
    GetWorkingEventListRequest,
    JsonPatchOperation,
    MatchEmployeeRequest,
    MatchEmployeeResponse,
    MergeRelativeRequest,
    MergeRelativeResponse,
    PatchRelativeResponse,
    WorkingEventList,
} from "@skbkontur/staff-common";
import { EmployeeListInitialState } from "@skbkontur/staff-selector-widget";

import { TrackCallback } from "../../Controls/Internals/Metrics/MetricsProviderContext";
import * as ModelPathUtils from "../../../../Common/ModelPath/ModelPath";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    createAbsoluteFromTokens,
    emptyAbsoluteModelPath,
    PathTokens,
} from "../../../../Common/ModelPath/ModelPath";
import { IterUtils } from "../../../../Common/IterableUtils";
import { isNotNullOrUndefined, noop, reject, runAsyncAction } from "../../../../Common/TypingUtils";
import { UnitActionType } from "../../Controls/FormParts/Unit/Store/unitActions";
import { SectionName, sectionNameToUnitId, UnitId } from "../../Controls/FormParts/Unit/UnitId/UnitId";
import { FormResources, ReloadableFormResources } from "../../Forms/FormResources";
import { createPublicForm } from "../../Forms/PublicForm/CreatePublicForm";
import { IPublicForm } from "../../Forms/PublicForm/IPublicForm";
import {
    autocalcsDisabledInReadOnly,
    FormSettings,
    getDraftFullId,
    getFormRootModelPath,
} from "../../Forms/Types/FormSettings";
import {
    addInstance,
    clearModel,
    copySubtree,
    removeInstance,
    removeInstances,
} from "../ActionCreators/AllActionCreators";
import { getErrorLog, receivedErrorlog, requestFailed } from "../ActionCreators/ErrorLogActionCreators";
import { setPanelActiveTab, setPanelState } from "../ActionCreators/HiddenPanelActionCreators";
import { CandyApiClient } from "../../../../Common/CandyApiClient/CandyApiClient";
import {
    ICandyApiClient,
    ICandyApiDraftClient,
    PathLoadSpecification,
} from "../../../../Common/CandyApiClient/ICandyApiClient";
import { EmptyTemplatesApiClient } from "../Api/TemplateApiClient/EmptyTemplatesApiClient";
import { ITemplatesApiClient } from "../Api/TemplateApiClient/ITemplatesApiClient";
import { KeWebTemplatesApiClientProxy } from "../Api/TemplateApiClient/KeWebTemplatesApiClientProxy";
import { CalculationFunctions, CalculationHooks } from "../Core/AutoCalculations/CalculationResources";
import { Calculator, ICalculator } from "../Core/AutoCalculations/Calculator";
import { Calculator2, ICalculator2 } from "../Core/Calculator2/Calculator2";
import { ChangeSetManager } from "../Core/ChangeSet/ChangeSetManager";
import { IChangeSetManager } from "../Core/ChangeSet/IChangeSetManager";
import { BindingPath, ViewModel, ViewModelFieldName } from "../Core/CoreTypes";
import { DataDeclaration } from "../Core/DataDeclaration/DataDeclaration";
import { DataDeclarationHelper } from "../Core/DataDeclaration/DataDeclarationHelper";
import { ErrorManager, IErrorManager } from "../Core/ErrorManager/ErrorManager";
import { ExternalEvent, ExternalEventPath } from "../Core/EventManagement/ExternalEvents/ExternalEvent";
import {
    ExternalEventManager,
    IExternalEventManager,
} from "../Core/EventManagement/ExternalEvents/ExternalEventManager";
import { ExternalHookFunction } from "../Core/EventManagement/ExternalEvents/ExternalHookFunction";
import { Localization, LocalizationDictionary } from "../Core/Localization/Localization";
import { IReadonlyModelHunk } from "../Core/ModelStoring/IReadonlyModelHunk";
import { IModel, Model } from "../Core/ModelStoring/Model";
import { ModelChunk, ReadonlyModelChunk } from "../Core/ModelStoring/ModelChunk";
import { ModelHunk } from "../Core/ModelStoring/ModelHunk";
import { Sections } from "../Core/ModelStoring/Sections";
import { ModelNormalizer } from "../Core/Normalization/ModelNormalizer";
import { MaskResolver } from "../Core/PathUtils/MaskResolver";
import { NavigationPathMapper } from "../Core/PathUtils/NavigationPathMapper";
import { PathUtils } from "../Core/PathUtils/PathUtils";
import { IPicklistsProvider } from "../Core/Picklists/IPicklistsProvider";
import { PicklistsProvider } from "../Core/Picklists/PicklistsProvider";
import { FormStore } from "../Core/Store/CandyStore";
import { IValidator, Validator } from "../Core/Validator/Validator";
import { ValidatorResources } from "../Core/Validator/ValidatorResources";
import { ActiveLogHelper } from "../Externals/ActiveLog/ActiveLogHelper";
import { IAttachmentsManager } from "../Externals/Attachments/IAttachmentsManager";
import { FocusProvider } from "../Externals/FocusProvider/FocusProvider";
import { IFocusProvider } from "../Externals/FocusProvider/IFocusProvider";
import { ControlRegistry, IControlRegistry } from "../Core/ComponentHandleRegistry/IControlRegistry";
import { SectionNames } from "../../../../Common/CommonConstants/SectionNames";
import { IFufManager } from "../Externals/FufManager/IFufManager";
import { ModelEvent, ModelEventsListener } from "../Core/EventManagement/ModelEvents/ModelEvent";
import { currentViewModelSelect, ViewModelSelect } from "../../Helpers/StateAccessors/ViewModelSelect";
import { getTestLoggerInstance, ITestLogger } from "../Testing/TestLogging/ITestLogger";
import { FufManager } from "../Externals/FufManager/FufManager";
import { FeatureFlags } from "../../Forms/Types/FeatureFlags";
import { iterateModelEntries } from "../../Controls/viewModelUtils";
import { CompareFunc, compareNumbers, ComparerDecorators, CompareResult } from "../../../../Common/Comparers";
import { INidusClient } from "../../../../Common/NidusClient/INidusClient";
import { NidusClientImpl } from "../../../../Common/NidusClient/NidusClientImpl";
import { FormTestFakes } from "../../Forms/Types/FormTestFakes";
import { ReadonlyModelOverStore } from "../Core/ModelStoring/ReadonlyModelOverStore";
import { IDigestApiClient } from "../../../../Common/CandyApiClient/DigestApiClient/IDigestApiClient";
import { createFormStore } from "../Core/Store/Store";
import { modelReducer } from "../Core/ModelStoring/Store/modelReducer";
import { withDebouncedBatching } from "../../../../Common/CandyApiClient/DigestApiClient/Decorators/DigestApiClientWithDebouncedBatching";
import { withCache } from "../../../../Common/CandyApiClient/DigestApiClient/Decorators/DigestApiClientWithCache";
import { Deferred } from "../../Helpers/Deferred";
import { ImportEmployeesActionType } from "../../StaffServiceIntegration/Employees/State/ImportEmployeesAction";
import { ILazyLoadingManager, LazyLoadingManager } from "../Core/LazyLoading/LazyLoadingManager";
import { LazyLoadingDeclaration } from "../Core/LazyLoading/LazyLoadingDeclaration";
import { loadCalculator2Resources } from "../../Forms/FormInitialization/LoadCalculator2Resources";
import { fetchRequisites, loadInner, prepareLazyLoadingManager } from "../../Forms/FormInitialization/DataFetching";
import { loadRequisitesIntoForm } from "../../Forms/FormInitialization/LoadingDataIntoForm";
import { ModelChangeListener } from "../../Forms/PublicForm/ModelChangeListener";
import { browserFetchWithOptionalPolyfill } from "../../../../Common/HttpClient/FetchImplementation/BrowserFetchWithOptionalPolyfill";
import { CustomNormalizationHelper } from "../Core/Normalization/CustomNormalizationHelper";
import { ImportEmployeesRequest } from "../../../../Common/CandyApiClient/Types/Staff/ImportEmployeesRequest";
import { ImportEmployeesOptions } from "../../../../Common/CandyApiClient/Types/Staff/ImportEmployeesOptions";
import { IIgnoredPathChecker } from "../Core/Calculator2/IIgnoredPathChecker";
import { Icon } from "../../Controls/Icons/Icon";
import { FormStaffClient, IFormStaffClient } from "../../StaffServiceIntegration/FormStaffClient";
import { delay } from "../../../../Common/AsyncUtils";
import { MassActionParameters } from "../../../../Common/CandyApiClient/Types/MassActions/MassActionParameters";
import { runFloatingAsyncAction } from "../../Helpers/TypingUtils";
import { ConflictConfirmationText } from "../../Controls/Services/ConflictConfirmationText";
import { track } from "../../Controls/Internals/Metrics/MetricsProvider";
import { CHANGE_SET_CONFLICT } from "../../Controls/metricsAction";
import { ModalOptions } from "../Core/ModalManager/IModalState";
import { showModalActionCreator } from "../Core/ModalManager/FormModalAction";
import { ClearPageMethod } from "../ActionCreators/UnitActionCreators";
import { formPageChangedEvent } from "../Core/EventManagement/ExternalEvents/ExternalEvents";
import { ErrorLogApi, IErrorLogApi } from "../Core/ErrorManager/ErrorLogApi";
import { parseErrorLog, serverErrorLogToGroupedAdapter } from "../Core/ErrorManager/Utils/Utils";
import { HelperFunctionsDateHelpers } from "../../Helpers/DateHelpers";
import { HelperFunctionsStringHelpers } from "../../Helpers/StringHelpers";
import { FSPrinterClient } from "../../../../Common/FSPrinterClient/FSPrinterClient";
import { IFSPrinterClient } from "../../../../Common/FSPrinterClient/IFSPrinterClient";
import { ResourcesOutdatedText } from "../../Controls/Services/ResourcesOutdatedText";
import { IPicklistApiClient } from "../../../../Common/PicklistApiClient/IPicklistApiClient";
import { PicklistApiClient } from "../../../../Common/PicklistApiClient/PicklistApiClient";
import { DigestApiClient } from "../../../../Common/CandyApiClient/DigestApiClient/DigestApiClient";
import { executionTimeMonitor } from "../../Controls/Internals/Metrics/ExecutionTimeMonitor";
import { isTestingMode } from "../../Controls/Testing";
import {
    EmployerNormalizedTag,
    ImportEmployerDocumentsOptions,
    ImportFromEmployeeServiceOptions,
} from "../../StaffServiceIntegration/EmployeeServiceTypes";
import {
    cancelImportDocuments,
    importDocuments,
} from "../../StaffServiceIntegration/Documents/State/EmployerDocumentsActionCreators";
import { getCandyHub } from "../../Forms/Sentry/InitializeSentry";
import { IExceptionLoggerParams } from "../../../../Common/HttpClient/HttpClient";
import { EmployeeRelativesActionType } from "../../StaffServiceIntegration/Relatives/State/EmployeeRelativesAction";
import { SyncErrorConfirmationText } from "../../Controls/Services/SyncErrorConfirmationText";
import { setFormReloading } from "../ActionCreators/FormActionCreators";
import { uuid } from "../../../../Common/UUID";
import { getExternalPropertyValue, PrimitiveValue } from "../../Helpers/StateAccessors/GetValueByPath";
import { getCandyApiHttpStatusHandlers } from "../Api/CandyApiClient/GetCandyApiHttpStatusHandlers";
import { FilteringManager } from "../Core/Filtering/FilteringManager";
import { ValueForInstancesSettingRule } from "../../../../Common/CandyApiClient/Types/MassActions/ValueForInstancesSettingRule";
import { ServerSideFilteringStrategy } from "../Core/Filtering/ServerSideFilteringStrategy";
import { ClientSideFilteringStrategy } from "../Core/Filtering/ClientSideFilteringStrategy";
import { NdsPlusApiClient } from "../../../../Common/NdsPlusApiClient/NdsPlusApiClient";
import { INdsPlusApiClient } from "../../../../Common/NdsPlusApiClient/INdsPlusApiClient";
import { ContractorsApiClient, IContractorsApiClient } from "../../ContractorsServiceIntegration/ContractorsApiClient";
import {
    GetContractorResponse,
    ImportContractorsWidgetOptions,
} from "../../ContractorsServiceIntegration/Types/ContractorsTypes";
import { ContractorsActionType } from "../../ContractorsServiceIntegration/State/ContractorsAction";
import { KCExpressionRuntime } from "../Core/Calculator2/KCExpressionRuntime";
import { EvaluationContext } from "../Core/Calculator2/EvaluationContext";
import { HighlightedControlWithFocus } from "../../Controls/ExternalHighlighting";

import { ICalculationFunctionCallContext } from "./CustomizationContexts/CalculatorContext/CalculationFunctionCallContext";
import { CalculationFunctionModelAccessor } from "./CustomizationContexts/CalculatorContext/CalculationFunctionModelAccessor";
import { CalculationFunctionValidator } from "./CustomizationContexts/CalculatorContext/CalculationFunctionValidator";
import { ICalculationFunctionCallContextProvider } from "./CustomizationContexts/CalculatorContext/ICalculationFunctionCallContextProvider";
import { IFetchFunctionCallContext } from "./CustomizationContexts/FetchFunctionContext/FetchFunctionCallContext";
import { FetchFunctionModelAccessor } from "./CustomizationContexts/FetchFunctionContext/FetchFunctionModelAccessor";
import { IFetchFunctionCallContextProvider } from "./CustomizationContexts/FetchFunctionContext/IFetchFunctionCallContextProvider";
import { HelperFunctionsModelAccessor } from "./CustomizationContexts/HelperFunctionContext/HelperFunctionsModelAccessor";
import { HelperFunctionsModelNormalizer } from "./CustomizationContexts/HelperFunctionContext/HelperFunctionsModelNormalizer";
import { HelperFunctionsStoreAccessor } from "./CustomizationContexts/HelperFunctionContext/HelperFunctionsStoreAccessor";
import { HelperFunctionsValidator } from "./CustomizationContexts/HelperFunctionContext/HelperFunctionsValidator";
import { IHelperFunctionsCallContext } from "./CustomizationContexts/HelperFunctionContext/IHelperFunctionsCallContext";
import { FormHookEventEmitter } from "./CustomizationContexts/HooksContext/FormHookEventEmitter";
import { FormHookModelAccessor } from "./CustomizationContexts/HooksContext/FormHookModelAccessor";
import { FormHookValidator } from "./CustomizationContexts/HooksContext/FormHookValidator";
import { IFormHookCallContext } from "./CustomizationContexts/HooksContext/IFormHookCallContext";
import { CustomValidationErrorManagerProxy } from "./CustomizationContexts/ValidatorContext/CustomValidationErrorManagerProxy";
import { CustomValidationFunctionValidationRunner } from "./CustomizationContexts/ValidatorContext/CustomValidationFunctionValidationRunner";
import { CustomValidationLocalizer } from "./CustomizationContexts/ValidatorContext/CustomValidationLocalizer";
import { CustomValidationModelAccessor } from "./CustomizationContexts/ValidatorContext/CustomValidationModelAccessor";
import { CustomValidationModelAccessorViaPublicForm } from "./CustomizationContexts/ValidatorContext/CustomValidationModelAccessorViaPublicForm";
import { CustomValidationPicklistsProvider } from "./CustomizationContexts/ValidatorContext/CustomValidationPicklistsProvider";
import { ICustomValidationFunctionCallContext } from "./CustomizationContexts/ValidatorContext/ICustomValidationFunctionCallContext";
import { ICustomValidationFunctionCallContextProvider } from "./CustomizationContexts/ValidatorContext/ICustomValidationFunctionCallContextProvider";
import {
    CalculationAwaitingOption,
    DependencyPathsCallback,
    FormHooks,
    HighlightedPathsCallback,
    IForm,
    RequisitesInfo,
} from "./IForm";
import { IFormReadonlyModelAccessor, IUpdateFormModelAccessor } from "./IFormModelAccessor";
import { UIUtil } from "./CustomizationContexts/HelperFunctionContext/UIUtil/UIUtil";
import { ModelChangeEventListener, ModelChangeHooks, Subscription } from "./ModelChangeEventListener";
import { ChangesProcessor, IChangesProcessor } from "./ChangesProcessor";
import { IExternalPropertiesProvider } from "./IExternalPropertiesProvider";

export interface FormInjectedProperties extends FormTestFakes {
    __onlyForTests__disableDebouncing?: boolean;
    requisites?: RequisitesInfo;
    publicForm?: IPublicForm;
    unmount?: () => void;
    dataDeclaration: DataDeclaration;
    lazyLoadDeclaration?: LazyLoadingDeclaration;
    calculationHooks: CalculationHooks;
    calculationFunctions: CalculationFunctions;
    validationFunctions: ValidatorResources;
    localizationDictionary?: LocalizationDictionary | undefined;
}

export class Form
    implements
        IForm,
        IFetchFunctionCallContextProvider,
        ICalculationFunctionCallContextProvider,
        ICustomValidationFunctionCallContextProvider,
        IExternalPropertiesProvider
{
    private readonly calculator: ICalculator;
    private readonly beforeModelChangeEventListener: ModelChangeEventListener;
    private readonly modelChangeEventListener: ModelChangeEventListener;
    public readonly nidusClient: INidusClient;
    public readonly testLogger: ITestLogger;
    public readonly localizationDictionary?: LocalizationDictionary;
    public readonly calculator2: ICalculator2;
    public readonly changeSet: IChangeSetManager;
    public readonly componentHandleRegistry: IControlRegistry;
    public readonly dataDeclarationHelper: DataDeclarationHelper;
    public readonly errorManager: IErrorManager;
    public readonly errorLogApi: IErrorLogApi;
    public readonly focusProvider: IFocusProvider;
    public readonly modelNormalizer: ModelNormalizer;
    public readonly navigationPathMapper: NavigationPathMapper;
    public readonly picklistsProvider: IPicklistsProvider;
    public readonly sections: Sections;
    public settings: FormSettings;
    public readonly validator: IValidator;
    public readonly localization: Localization;
    public readonly candyApi: ICandyApiClient;
    public readonly contractorsClient: IContractorsApiClient;
    public readonly fsPrinterClient: IFSPrinterClient;
    public readonly staffClient: IFormStaffClient;
    public readonly candyApiDraftClient: ICandyApiDraftClient;
    public readonly picklistApiClient: IPicklistApiClient;
    public readonly templatesApi: ITemplatesApiClient;
    private readonly externalEventManager: IExternalEventManager;
    public readonly publicForm: IPublicForm;
    public readonly fufManager!: IFufManager;
    private resourcesHash: string;
    public readonly requisiteList: string[];
    public readonly store: FormStore;
    public readonly lazyLoadingManager: ILazyLoadingManager;
    public readonly customNormalizationHelper: CustomNormalizationHelper;
    public readonly filteringManager: FilteringManager;
    public readonly useServerSideCalculations: boolean;
    public readonly attachmentsPublicForms: { [attachmentId: string]: IPublicForm };
    public readonly ndsPlusApiClient: INdsPlusApiClient;

    public featureFlags: FeatureFlags = {};
    public ieSizeError: boolean;
    public hooks!: FormHooks;
    public requisites!: RequisitesInfo;
    public unmount: () => void;
    public activeLogHelper!: ActiveLogHelper;
    public attachments!: IAttachmentsManager;

    private readonly customValidationFunctionCallContext: Omit<
        ICustomValidationFunctionCallContext,
        "model" | "requisites"
    >;
    private readonly formHookCallContext: IFormHookCallContext;
    private errorCountStateObserverUnsubscribe?: Unsubscribe;
    private readonly errorCountChangeListeners: ModelEventsListener[] = [];
    private readonly modelOverStore: IFormReadonlyModelAccessor;
    private isInConflictResolving = false;
    private readonly changingProcessor: IChangesProcessor;
    private highlightedPath: AbsoluteResolvedModelPath = emptyAbsoluteModelPath();
    private readonly dependencyPathsChangedSubscriptions = new Map<string, DependencyPathsCallback>();
    private readonly highlightedControlsChangedSubscriptions = new Map<string, HighlightedPathsCallback>();
    public readonly track: TrackCallback;

    public constructor(
        injected: FormInjectedProperties,
        settings: FormSettings,
        requisiteList: string[],
        resourcesHash: string
    ) {
        this.resourcesHash = resourcesHash;
        this.settings = settings;
        this.attachmentsPublicForms = {};
        this.store = createFormStore(this, modelReducer);
        this.modelOverStore = new ReadonlyModelOverStore(this.store);
        this.requisiteList = requisiteList;
        this.testLogger = getTestLoggerInstance();
        this.componentHandleRegistry = new ControlRegistry();
        this.externalEventManager = new ExternalEventManager();
        this.modelChangeEventListener = new ModelChangeEventListener();
        this.beforeModelChangeEventListener = new ModelChangeEventListener();
        this.ieSizeError = false;
        this.unmount = noop;
        this.useServerSideCalculations =
            (settings.useServerSideFLangNormalization ?? false) && (settings.useServerSideCalculations ?? false);

        this.track = (action, options) => {
            const category = "candy";
            const contextData = {
                gfv: settings.gfv,
                ns: settings.ns,
                draftId: settings.draftId,
            };
            track(category, action, { ...contextData, ...options });
        };

        for (const injectedProp of Object.keys(injected)) {
            // @ts-ignore
            this[injectedProp] = injected[injectedProp];
        }
        this.nidusClient = new NidusClientImpl(
            browserFetchWithOptionalPolyfill,
            settings.nidusUrl,
            settings.authentication,
            (error, params) => this.logApiClientExceptionToSentry(error, params)
        );
        this.settings.staffWidgetUrl = inferStaffWidgetUrlFromKeformsUrl(settings.keformsUrl);
        // eslint-disable-next-line no-warning-comments
        // todo use inferStaffWidgetUrlFromKeformsUrl()
        this.settings.contractorsWidgetUrl = inferContractorsWidgetUrlFromKeformsUrl(settings.keformsUrl);
        this.localization = new Localization(settings.lang, injected.localizationDictionary);
        this.candyApi =
            injected.__onlyForTests__candyApi ??
            new CandyApiClient(
                browserFetchWithOptionalPolyfill,
                settings.keformsUrl,
                settings.resourceZone,
                settings.authentication,
                (error, params) => this.logApiClientExceptionToSentry(error, params),
                getCandyApiHttpStatusHandlers(this.settings.theme)
            );

        this.staffClient = new FormStaffClient(this.settings);

        this.contractorsClient = new ContractorsApiClient(this.candyApi, this.settings);

        this.candyApiDraftClient = this.candyApi.forDraft(getDraftFullId(this.settings));

        this.fsPrinterClient = new FSPrinterClient(
            browserFetchWithOptionalPolyfill,
            this.settings.fsPrinterUrl,
            settings.resourceZone,
            settings.authentication,
            this.candyApiDraftClient,
            (error, params) => this.logApiClientExceptionToSentry(error, params)
        );

        this.picklistApiClient = new PicklistApiClient(
            browserFetchWithOptionalPolyfill,
            this.settings.picklistUrl,
            settings.resourceZone,
            settings.authentication,
            (error, params) => this.logApiClientExceptionToSentry(error, params)
        );

        this.ndsPlusApiClient = new NdsPlusApiClient(
            browserFetchWithOptionalPolyfill,
            this.settings.ndsPlusApiUrl,
            settings.resourceZone,
            settings.authentication,
            (error, params) => this.logApiClientExceptionToSentry(error, params)
        );

        if (settings.templatesApi != undefined) {
            this.templatesApi = new KeWebTemplatesApiClientProxy(settings.templatesApi);
        } else {
            this.templatesApi = new EmptyTemplatesApiClient();
        }

        this.dataDeclarationHelper = new DataDeclarationHelper(this, injected.dataDeclaration, settings.formRoot);
        this.sections = new Sections(this.dataDeclarationHelper, this.externalEventManager);
        this.modelNormalizer = new ModelNormalizer(
            this,
            this.dataDeclarationHelper,
            this.sections,
            this.settings.isReadOnly ?? false
        );

        this.changeSet =
            injected.__onlyForTests__changeSet ??
            new ChangeSetManager(
                this.candyApiDraftClient,
                () => this.store,
                () => this.onConflictDetected(),
                this.useServerSideCalculations,
                this.attachmentsPublicForms,
                this.externalEventManager,
                this
            );
        this.fufManager = new FufManager(this.store, this.candyApiDraftClient, this);
        this.errorManager = new ErrorManager();
        this.errorLogApi = new ErrorLogApi(this);
        this.calculator = new Calculator(
            this,
            this.dataDeclarationHelper,
            this.sections,
            this.errorManager,
            injected.calculationFunctions,
            injected.calculationHooks
        );
        const ignoredPathChecker: IIgnoredPathChecker = {
            isIgnoredPath: path => this.sections.isIgnoredPathByMasks(path),
        };
        const digestApiClient = this.createDigestApiClient(injected);
        this.picklistsProvider = new PicklistsProvider(digestApiClient, this.modelOverStore);
        this.calculator2 = new Calculator2(
            this.errorManager,
            this.dataDeclarationHelper,
            ignoredPathChecker,
            this.picklistsProvider,
            this
        );
        this.validator = new Validator(
            this,
            this.localization,
            this.store,
            this.errorManager,
            this.picklistsProvider,
            this.calculator2,
            injected.validationFunctions,
            settings.__onlyForTests__enableLegacyValidators ?? false
        );
        this.lazyLoadingManager = new LazyLoadingManager(
            injected.lazyLoadDeclaration ?? {},
            {
                loadInner: paths =>
                    loadInner(this.candyApiDraftClient, this.settings.useServerSideFLangNormalization, paths),
            },
            {
                apply: (data: IReadonlyModelHunk) => this.performFormLazyLoad(data),
            },
            {
                sendCurrentChanges: () => this.changeSet.send(),
                getViewModel: path => this.getViewModel(path),
            }
        );
        this.navigationPathMapper = new NavigationPathMapper(
            this,
            this.dataDeclarationHelper,
            this.lazyLoadingManager,
            this.candyApiDraftClient
        );
        this.focusProvider = new FocusProvider(this.candyApi, getDraftFullId(settings));
        this.publicForm = createPublicForm(this);

        this.customValidationFunctionCallContext = {
            errorManager: new CustomValidationErrorManagerProxy(this),
            localization: new CustomValidationLocalizer(this.localization),
            pathUtils: PathUtils,
            publicForm: new CustomValidationModelAccessorViaPublicForm(this.publicForm),
            settings: settings,
            validator: new CustomValidationFunctionValidationRunner(this),
            picklistsProvider: new CustomValidationPicklistsProvider(this.picklistsProvider),
            calculationFunctions: {
                // Диадок наглым образом ломится в запретную зону, ограничено так, чтобы работали текущие формы
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                generateFirstErrorPart: injected.calculationFunctions?.generateFirstErrorPart as any,
            },
        };
        this.formHookCallContext = {
            model: new FormHookModelAccessor(this),
            // @ts-ignore publicForm появляется позже, исправить
            publicForm: undefined,
            validator: new FormHookValidator(this),
            settings: settings,
            eventManager: new FormHookEventEmitter(this.externalEventManager),
        };
        this.customNormalizationHelper = new CustomNormalizationHelper(this);

        this.filteringManager = new FilteringManager(
            this.dataDeclarationHelper,
            this.lazyLoadingManager,
            () => this.changeSet.send(),
            new ClientSideFilteringStrategy(() => this.store.getState()),
            new ServerSideFilteringStrategy(() => this.store.getState(), this.candyApiDraftClient),
            () => {
                const lastLayerIndex = (this.store.getState().redefinedViewModels?.length ?? 0) - 1;
                const lastLayerIndexOrUndefined = lastLayerIndex >= 0 ? lastLayerIndex : undefined;
                const viewModelSelect = new ViewModelSelect(
                    lastLayerIndexOrUndefined != undefined,
                    lastLayerIndexOrUndefined
                );
                return {
                    getChildren: bindingPath => viewModelSelect.children(this.store.getState(), bindingPath),
                };
            },
            () => {
                const lastLayerIndex = (this.store.getState().redefinedViewModels?.length ?? 0) - 1;
                return lastLayerIndex >= 0 ? lastLayerIndex : undefined;
            }
        );

        this.changingProcessor = new ChangesProcessor(
            this.calculator,
            this.calculator2,
            this.store,
            this.modelOverStore,
            this.changeSet,
            this.settings,
            this.candyApiDraftClient,
            this.sections,
            this.modelNormalizer,
            this.dataDeclarationHelper,
            this.lazyLoadingManager,
            this.filteringManager,
            this.useServerSideCalculations
        );
    }

    public getExternalProperty(path: string): PrimitiveValue | undefined {
        if (!path.startsWith("@")) {
            throw new Error("External property must have leading '@' sign");
        }
        return getExternalPropertyValue(this.store.getState(), {}, path);
    }

    public onBeforeModelChange(path: BindingPath | BindingPath[], listener: ModelChangeListener): Subscription {
        return this.beforeModelChangeEventListener.onModelChange(path, listener);
    }

    public onModelChange(path: BindingPath | BindingPath[], listener: ModelChangeListener): Subscription {
        return this.modelChangeEventListener.onModelChange(path, listener);
    }

    public getBeforeModelChangeEventListenerHooks(): ModelChangeHooks {
        return this.beforeModelChangeEventListener.modelChangeHookListener;
    }

    public getModelChangeEventListenerHooks(): ModelChangeHooks {
        return this.modelChangeEventListener.modelChangeHookListener;
    }

    private createDigestApiClient(injected: FormInjectedProperties): IDigestApiClient {
        let digestApiClient: IDigestApiClient =
            injected.__onlyForTests__digestApi ?? new DigestApiClient(this.picklistApiClient);
        if (!injected.__onlyForTests__disableDebouncing) {
            digestApiClient = withDebouncedBatching(digestApiClient, 100);
        }
        return withCache(digestApiClient);
    }

    private logApiClientExceptionToSentry(error: unknown, params?: IExceptionLoggerParams): void {
        const sentry = getCandyHub();

        if (params?.fetchUrl != undefined) {
            sentry?.addBreadcrumb({
                level: "log",
                data: {
                    fetchUrl: params.fetchUrl,
                },
                category: "HttpClient",
            });
            sentry?.setTag("fetchUrl", params.fetchUrl);
        }

        sentry?.captureException(error);
    }

    public addInstance(path: BindingPath, filteredUniqKey?: string): string {
        return this.store.dispatch(addInstance(path, undefined, undefined, undefined, undefined, filteredUniqKey));
    }

    public removeInstance(path: BindingPath, instance: string, filteredUniqKey?: string): void {
        this.store.dispatch(removeInstance(path, instance, undefined, filteredUniqKey));
    }

    public removeInstances(path: BindingPath, instances: readonly string[], filteredUniqKey?: string): void {
        this.store.dispatch(removeInstances(path, instances, undefined, filteredUniqKey));
    }

    public copySubtree(
        sourceBindingPath: BindingPath | AbsoluteResolvedModelPath,
        targetBindingPath: BindingPath | AbsoluteResolvedModelPath,
        doNotCopyPaths?: string[]
    ): void {
        copySubtree(this, sourceBindingPath, targetBindingPath, doNotCopyPaths);
    }

    public setValue(bindingPath: BindingPath | AbsoluteResolvedModelPath, value: string | undefined): void {
        this.setViewModelField(bindingPath, "value", value);
    }

    public setViewModelField(
        bindingPath: BindingPath | AbsoluteResolvedModelPath,
        fieldName: ViewModelFieldName,
        value: unknown
    ): void {
        if (fieldName === "children") {
            // @ts-ignore Вставить провеку типов по хорошему, но пока я ссу
            this.setChildren(bindingPath, value);
        } else {
            this.performModelUpdate(model => model.setViewModelField(bindingPath, fieldName, value));
        }
    }

    public setChildren(bindingPath: BindingPath | AbsoluteResolvedModelPath, children: string[]): void {
        this.performModelUpdate(model => model.setChildren(bindingPath, children));
    }

    public setBatch(batch: ReadonlyModelChunk): void {
        this.performModelUpdate(model => model.setBatch(batch));
    }

    public getBatch(path: BindingPath): ModelChunk {
        return this.performModelRead(model => model.getBatch(path));
    }

    public setBatchByPrefix(prefix: BindingPath | AbsoluteResolvedModelPath, batch: ReadonlyModelChunk): void {
        this.performModelUpdate(model => model.setBatchByPrefix(prefix, batch));
    }

    public getCustomValidationFunctionCallContext(model: IModel): ICustomValidationFunctionCallContext {
        const result: ICustomValidationFunctionCallContext = {
            ...this.customValidationFunctionCallContext,
            requisites: this.requisites,
            model: new CustomValidationModelAccessor(this.customValidationFunctionCallContext, model),
        };
        return result;
    }

    public getCalculationFunctionCallContext(model: IModel): ICalculationFunctionCallContext {
        return {
            model: new CalculationFunctionModelAccessor(model),
            validator: new CalculationFunctionValidator(this),
            pathUtils: PathUtils,
            requisites: this.requisites,
            settings: this.settings,
        };
    }

    public getFetchFunctionCallContext(model: IModel | undefined): IFetchFunctionCallContext {
        return {
            model: new FetchFunctionModelAccessor(model ?? this.modelOverStore),
            pathUtils: PathUtils,
            requisites: this.requisites,
            settings: this.settings,
            __INTERNAL__getDefaultAutoFlag: this.__INTERNAL__getDefaultAutoFlag,
            __INTERNAL__getSectionFunction: this.__INTERNAL__getSectionFunction,
            expressionRuntime: new KCExpressionRuntime(
                new EvaluationContext(
                    model ?? this.createModel(),
                    new ErrorManager(),
                    [],
                    {
                        isIgnoredPath: () => false,
                    },
                    this.picklistsProvider,
                    this
                )
            ),
            ap: createAbsoluteFromTokens,
            each: PathTokens.each,
            current: PathTokens.current,
        };
    }

    public getHelperFunctionsCallContext(): IHelperFunctionsCallContext {
        return {
            pathUtils: PathUtils,
            modelPathUtils: ModelPathUtils,
            model: new HelperFunctionsModelAccessor(this),
            modelNormalizer: new HelperFunctionsModelNormalizer(),
            store: new HelperFunctionsStoreAccessor(this.store),
            validator: new HelperFunctionsValidator(this),
            publicForm: this.publicForm,
            React: React,
            ReactDOM: ReactDOM,
            uiUtil: new UIUtil(this.settings.theme),
            "react-ui": {
                Button: Button,
                Input: Input,
                ComboBox: ComboBox,
                Gapped: Gapped,
                Hint: Hint,
                Tooltip: CandyTooltip,
                Icon: Icon,
                Link: Link,
                MenuSeparator: MenuSeparator,
                Radio: Radio,
                Toast: Toast,
                MenuItem: MenuItem,
                Checkbox: Checkbox,
                Modal: Modal,
            },
            typedHelpers: {
                dateHelpers: HelperFunctionsDateHelpers,
                stringHelpers: HelperFunctionsStringHelpers,
            },
        };
    }

    public getFormHookCallContext(): IFormHookCallContext {
        return { ...this.formHookCallContext, publicForm: this.publicForm };
    }

    public triggerExternalEvent(event: ExternalEvent): void {
        this.externalEventManager.triggerExternalEvent(event);
    }

    public triggerExternalEvents(events: ExternalEvent[]): void {
        this.externalEventManager.triggerExternalEvents(events);
    }

    public offExternalEvent(pathArr: ExternalEventPath, x2: ExternalHookFunction): void {
        this.externalEventManager.offExternalEvent(pathArr, x2);
    }

    public onExternalEvent(pathArr: ExternalEventPath, x2: ExternalHookFunction): void {
        this.externalEventManager.onExternalEvent(pathArr, x2);
    }

    public onceExternalEvent(pathArr: ExternalEventPath, x2: ExternalHookFunction): ExternalHookFunction {
        return this.externalEventManager.onceExternalEvent(pathArr, x2);
    }

    public hasExternalEventSubscriber(pathArr: ExternalEventPath): boolean {
        return this.externalEventManager.hasExternalEventSubscriber(pathArr);
    }

    public disableAllValidations(): void {
        this.validator.disableAllValidations();
    }

    public enableAllValidations(): void {
        this.validator.enableAllValidations();
    }

    public async recalculateTargets(targetPaths: readonly string[]): Promise<void> {
        await this.performModelUpdate(model => this.calculator2.recalculateTargets(model, targetPaths));
    }

    public async revalidatePaths(paths: AbsoluteModelPath[]): Promise<void> {
        await this.changingProcessor.revalidatePaths(paths);
    }

    public async runCheckFromPublicForm(): Promise<void> {
        this.store.dispatch(setPanelState("pin"));
        this.store.dispatch(setPanelActiveTab("check"));
        await this.store.dispatch(getErrorLog);
    }

    public setErrorLogForTestsOnly(errorLog: ServerErrorLog): void {
        try {
            this.store.dispatch(
                receivedErrorlog(
                    parseErrorLog(serverErrorLogToGroupedAdapter(errorLog), () => this.navigationPathMapper)
                )
            );
        } catch {
            this.store.dispatch(requestFailed());
        }
    }

    public showCustomModal(options: ModalOptions): void {
        this.store.dispatch(showModalActionCreator(options));
    }

    public getChildren(bindingPath: BindingPath | AbsoluteResolvedModelPath): string[] | undefined {
        return this.performModelRead(model => model.getChildren(bindingPath));
    }

    public getChildrenPaths(path: AbsoluteModelPath): Iterable<AbsoluteResolvedModelPath> {
        return this.performModelRead(model => MaskResolver.getPathsByPrefix(this.dataDeclarationHelper, model, path));
    }

    public getValue(bindingPath: BindingPath | AbsoluteResolvedModelPath): string | undefined {
        return this.performModelRead(model => model.getValue(bindingPath));
    }

    public getViewModel(bindingPath: BindingPath | AbsoluteResolvedModelPath): ViewModel | undefined {
        return this.performModelRead(model => model.getViewModel(bindingPath));
    }

    public getViewModelField(bindingPath: BindingPath, fieldName: ViewModelFieldName): ViewModel | undefined {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.performModelRead(model => model.getViewModelField(bindingPath, fieldName));
    }

    public async renormalizePage(sectionName: SectionName, instancePath: AbsoluteResolvedModelPath): Promise<void> {
        this.performModelUpdate(model => this.clearInstanceSectionInternal(model, sectionName, instancePath));
        await this.normalizeSection(sectionName);
    }

    public async clearSection(
        sectionName: SectionName,
        bindingPath: AbsoluteResolvedModelPath | undefined,
        method: ClearPageMethod
    ): Promise<void> {
        if (bindingPath != undefined && method === ClearPageMethod.ParentAndChildren) {
            this.performModelUpdate(model => model.removeByPrefix(bindingPath));
            this.clearInnerSections(sectionName, bindingPath);
        } else {
            this.performModelUpdate(model => this.clearSectionInternal(model, sectionName));
        }

        await this.normalizeSection(sectionName);
    }

    public removeSection(sectionName: SectionName, bindingPath?: AbsoluteResolvedModelPath): void {
        this.performModelUpdate(model => this.removeSectionInternal(model, sectionName, bindingPath));
        this.clearInnerSections(sectionName, bindingPath);
    }

    private clearInnerSections(sectionName: SectionName, bindingPath: AbsoluteResolvedModelPath | undefined) {
        if (bindingPath == undefined) {
            return;
        }

        const parentInstances = Iterator.from(bindingPath.getPathParts())
            .filter(part => PathTokens.isInstanceToken(part))
            .toArray()
            .reverse();
        const changedSections = this.getSectionsByPath(bindingPath, parentInstances);
        for (const section of changedSections) {
            if (section !== sectionName) {
                this.sections.markSectionAsIgnored(section);
            }

            this.triggerExternalEvent(formPageChangedEvent(section));
        }
    }

    public getSectionsByPath(bindingPath: AbsoluteResolvedModelPath, parentInstances: string[]): SectionName[] {
        const optionalSections = this.dataDeclarationHelper.getOptionalSections();
        const masks = Iterator.from(MaskResolver.getPathsByPrefix(this.dataDeclarationHelper, this, bindingPath))
            .map(path => this.dataDeclarationHelper.getMask(path))
            .toArray();
        return optionalSections
            .filter(section => masks.some(mask => mask === section.path))
            .map(section => [section.id].concat(parentInstances).join("-") as SectionName);
    }

    private clearInstanceSectionInternal(
        model: IModel,
        sectionName: SectionName,
        instancePath: AbsoluteResolvedModelPath
    ): void {
        const instanceLegacyPath = instancePath.toLegacyPath();
        const optionalSections = this.dataDeclarationHelper.getOptionalSections();
        const paths = Iterator.from(this.sections.paths(model, sectionName))
            .filter(path => path.toLegacyPath().startsWith(instanceLegacyPath))
            .toArray();
        const unitId = sectionNameToUnitId(sectionName);
        if (optionalSections.some(x => x.id === unitId.pageId)) {
            this.sections.markSectionAsIgnored(sectionName);
        }
        for (const path of paths) {
            model.remove(path);
        }
    }

    private clearSectionInternal(model: IModel, sectionName: SectionName): void {
        const optionalSections = this.dataDeclarationHelper.getOptionalSections();
        const paths = Iterator.from(this.sections.paths(model, sectionName)).toArray();
        const unitId = sectionNameToUnitId(sectionName);
        if (optionalSections.some(x => x.id === unitId.pageId)) {
            this.sections.markSectionAsIgnored(sectionName);
        }
        for (const path of paths) {
            model.remove(path);
        }
    }

    private removeSectionInternal(
        model: IModel,
        sectionName: SectionName,
        bindingPath?: AbsoluteResolvedModelPath
    ): void {
        const optionalSections = this.dataDeclarationHelper.getOptionalSections();
        const unitId = sectionNameToUnitId(sectionName);
        if (optionalSections.some(x => x.id === unitId.pageId)) {
            this.sections.markSectionAsIgnored(sectionName);
        }
        if (bindingPath != undefined) {
            model.removeByPrefix(bindingPath);
        } else {
            const paths = Iterator.from(this.sections.paths(model, sectionName)).toArray();
            for (const path of paths) {
                model.remove(path);
            }
        }
    }

    public remove(bindingPath: AbsoluteResolvedModelPath): void {
        this.performModelUpdate(model => model.remove(bindingPath));
    }

    public removeByPrefix(bindingPath: AbsoluteResolvedModelPath | AbsoluteResolvedModelPath[]): void {
        if (Array.isArray(bindingPath)) {
            this.performModelUpdate(model => {
                bindingPath.forEach(path => {
                    model.removeByPrefix(path);
                });
            });
        } else {
            this.performModelUpdate(model => model.removeByPrefix(bindingPath));
        }
    }

    public updateModel(updateFn: (model: IUpdateFormModelAccessor) => void): void {
        this.performModelUpdate(model => updateFn(model));
    }

    public normalizeByPrefix(nodePathPrefix: AbsoluteResolvedModelPath): void {
        this.performModelUpdate(model => this.modelNormalizer.normalizeByPrefix(model, nodePathPrefix));
    }

    public async normalizeSection(sectionName: SectionName): Promise<void> {
        this.performModelUpdate(model => this.modelNormalizer.normalizeSection(model, sectionName));
        if (this.useServerSideCalculations) {
            const paths = Iterator.from(
                this.performModelRead(model => this.sections.paths(model, sectionName))
            ).toArray();
            await this.customNormalize(null, paths);
        } else if (!this.settings.useServerSideCalculations || this.changeSet.isRedefined()) {
            await this.performModelUpdate(model => {
                const paths = this.sections.getSectionPathsForCalculations(model, sectionName);
                return this.calculator2.recalculateTargets(
                    model,
                    Iterator.from(paths)
                        .map(x => x.toLegacyPath())
                        .toArray()
                );
            });
        }
    }

    public setError(path: BindingPath | AbsoluteResolvedModelPath, errorMessage: string): void {
        this.performModelUpdate(model => this.errorManager.clearErrorsCached(model, path));
        this.performModelUpdate(model => this.errorManager.addErrorCached(model, path, errorMessage));
    }

    public setWarning(bindingPath: BindingPath, warningMessage: string): void {
        this.performModelUpdate(model => this.errorManager.clearWarningsCached(model, bindingPath));
        this.performModelUpdate(model => this.errorManager.addWarningCached(model, bindingPath, warningMessage));
    }

    public addError(nodePath: BindingPath | AbsoluteResolvedModelPath, message: string): void {
        this.performModelUpdate(model => this.errorManager.addErrorCached(model, nodePath, message));
    }

    public addWarning(nodePath: string, message: string): void {
        this.performModelUpdate(model => this.errorManager.addWarningCached(model, nodePath, message));
    }

    public clearWarnings(nodePath: string): void {
        this.performModelUpdate(model => this.errorManager.clearWarningsCached(model, nodePath));
    }

    public clearErrors(nodePath: string): void {
        this.performModelUpdate(model => this.errorManager.clearErrorsCached(model, nodePath));
    }

    public removeWarning(nodePath: string, message: string): void {
        this.performModelUpdate(model => this.errorManager.removeWarning(model, nodePath, message));
    }

    public async getAdditionalEmployeeInfo(
        query: GetEmployeesWithActivePeriodQuery
    ): Promise<EmployeeList | undefined> {
        const additionalInfo = await this.staffClient.getAdditionalEmployeeInfo(query);
        return additionalInfo;
    }

    public getEmployees(query: GetEmployeesWithActivePeriodQuery): Promise<EmployeeList> {
        return this.staffClient.getEmployees(query);
    }

    public async mergeRelative(
        bindingPath: BindingPath,
        employeeId: string,
        query: MergeRelativeRequest
    ): Promise<MergeRelativeResponse | undefined> {
        if (!employeeId) {
            return undefined;
        }

        try {
            const mergeRelativeResponse = await this.staffClient.mergeRelative(employeeId, query);
            if (!mergeRelativeResponse.relative) {
                throw new Error("Ошибка добавления родственника");
            }
            this.store.dispatch({
                type: EmployeeRelativesActionType.InsertAddedRelative,
                payload: {
                    path: bindingPath,
                    relative: mergeRelativeResponse.relative,
                },
            });

            return mergeRelativeResponse;
        } catch (e) {
            Toast.push("Ошибка при добавлении родственника");
            // eslint-disable-next-line no-console
            console.error(e);
            getCandyHub().captureException(e);
        }

        return undefined;
    }

    public patchRelative(
        employeeId: string,
        relativeId: string,
        operations: JsonPatchOperation[]
    ): Promise<PatchRelativeResponse> {
        return this.staffClient.patchRelative(employeeId, relativeId, operations);
    }

    public matchEmployee(query: MatchEmployeeRequest): Promise<MatchEmployeeResponse> {
        return this.staffClient.matchEmployee(query);
    }

    public async getWorkingEventsForEmployee(query: GetWorkingEventListRequest): Promise<WorkingEventList | undefined> {
        const workingEvents = await this.staffClient.getWorkingEventsForEmployee(query);
        return workingEvents;
    }

    public async replaceResources(prevResources: FormResources, resources: ReloadableFormResources): Promise<void> {
        if (resources.requisiteList != undefined) {
            const requisites = await fetchRequisites(
                this.candyApiDraftClient,
                resources.requisiteList,
                getDraftFullId(this.settings)
            );
            loadRequisitesIntoForm(this, requisites);
        }
        if (resources.validationFunctions != undefined || resources.autocalculationConfigurators != undefined) {
            this.calculator2.clearFunctions();
            this.validator.loadValidationFunctions(resources.validationFunctions ?? prevResources.validationFunctions);
            loadCalculator2Resources(
                this,
                resources.autocalculationConfigurators ?? prevResources.autocalculationConfigurators
            );
        }
        if (resources.dataDeclaration != undefined) {
            this.dataDeclarationHelper.reloadDataDeclaration(this.settings.formRoot, resources.dataDeclaration);
        }
        if (resources.calculationFunctions != undefined && resources.calculationHooks != undefined) {
            this.calculator.loadFunctions(resources.calculationHooks, resources.calculationFunctions);
        }
        if (resources.resourcesHash != undefined) {
            this.resourcesHash = resources.resourcesHash;
        }
        const model = this.createModel();
        await this.changingProcessor.processEventsAfterReplaceResources(model);
    }

    public async performInitialFormLoad(
        data: IReadonlyModelHunk,
        settings: FormSettings,
        clearBeforeInit: boolean = false
    ): Promise<void> {
        const model = this.createModel();
        const context1 = model.beginGetEvents();
        if (settings.doNotValidateFormOnStart || settings.isReadOnly) {
            this.disableAllValidations();
        }
        if (clearBeforeInit) {
            this.store.dispatch(clearModel());
        }
        model.setBatch(data);
        model.flushIntoStore(this.store);

        const optionalSections = this.dataDeclarationHelper.getOptionalSections();
        if (optionalSections.length > 0) {
            this.sections.resolveOptionalSections(data);
        }

        if (!autocalcsDisabledInReadOnly(this.settings)) {
            await this.changingProcessor.processEventsAfterInitialLoad(model, context1.releaseAndGetEvent());
        }

        if (settings.doNotValidateFormOnStart) {
            this.enableAllValidations();
        }
    }

    public async performFormLoad(data: IReadonlyModelHunk, clearBeforeInit: boolean = false): Promise<void> {
        const model = this.createModel();
        if (clearBeforeInit) {
            this.store.dispatch(clearModel());
        }
        model.setBatch(data);
        const optionalSections = this.dataDeclarationHelper.getOptionalSections();
        if (optionalSections.length > 0) {
            this.sections.resolveOptionalSections(data);
        }
        model.flushIntoStore(this.store);
        if (!autocalcsDisabledInReadOnly(this.settings)) {
            await this.changingProcessor.processEventsAfterFormLoad(model);
        }
    }

    public performFormLazyLoad(data: IReadonlyModelHunk): void {
        const model = this.createModel();
        model.setBatch(data);
        const optionalSections = this.dataDeclarationHelper.getOptionalSections();
        if (optionalSections.length > 0) {
            this.sections.resolveOptionalSections(data);
        }
        model.flushIntoStore(this.store);

        if (!autocalcsDisabledInReadOnly(this.settings)) {
            this.changingProcessor.processEventsAfterLazyLoad(model);
        }
    }

    public async reloadInner(): Promise<void> {
        this.store.dispatch(setFormReloading(true));
        try {
            await this.syncDataVersion();
            await this.changeSet.send();
            await this.lazyLoadingManager.clearState();
            await prepareLazyLoadingManager(this);
            const paths = IterUtils.concat<PathLoadSpecification>(
                [{ pathOrPathPrefix: "*", skip: false }],
                Iterator.from(this.lazyLoadingManager.getServerOnlyRules()).map(x => ({
                    pathOrPathPrefix: x.modelPath,
                    skip: x.skip,
                })),
                this.lazyLoadingManager.isLazyLoadingEnabled()
                    ? Iterator.from(this.lazyLoadingManager.getInitialLazyLoadRules()).map(x => ({
                          pathOrPathPrefix: x.modelPath,
                          skip: x.skip,
                      }))
                    : []
            ).toArray();

            const data = await loadInner(
                this.candyApiDraftClient,
                this.settings.useServerSideFLangNormalization,
                paths
            );
            await this.syncDataVersion();
            this.sections.markAllAsIgnored();
            await this.loadDataIntoForm(data, true);
            await this.filteringManager.requestFilteringAllMultilines();
        } catch (e) {
            this.logApiClientExceptionToSentry(e);
            const uiUtil = new UIUtil(this.settings.theme);
            const reloadOrUpdateConfirmation = await uiUtil.confirm(
                SyncErrorConfirmationText(),
                "Обновить",
                "Закрыть",
                "Введенные данные не сохранятся"
            );
            if (reloadOrUpdateConfirmation) {
                window.location.reload();
            }
        } finally {
            this.store.dispatch(setFormReloading(false));
        }
    }

    private async syncDataVersion(): Promise<void> {
        const dataVersion = await this.candyApiDraftClient.getDataVersion();
        this.changeSet.setCurrentDataVersion(dataVersion);
    }

    @executionTimeMonitor("loadDataIntoForm", isTestingMode() ? Infinity : 20000)
    public async loadDataIntoForm(data: IReadonlyModelHunk, clearBeforeInit: boolean = false): Promise<void> {
        if (isResourcesAreSame(data, this.settings, this.resourcesHash)) {
            await this.performFormLoad(data, clearBeforeInit);
        } else {
            if (this.settings.useServerSideFLangNormalization) {
                await this.showOutdatedResourceAlertAndReload();
            } else {
                await this.performInitialFormLoad(data, this.settings, clearBeforeInit);
                this.setViewModelField(this.settings.formRoot, "resourcesHash", this.resourcesHash);
                this.setViewModelField(this.settings.formRoot, "normalizationSource", "client");
            }
        }
    }

    public getDependsOnPaths(path: AbsoluteModelPath): AbsoluteModelPath[] {
        const calculatorDeps = this.calculator.getArgumentsPaths(path);
        const calculator2Deps = this.calculator2.getArgumentsPaths(path);
        return calculatorDeps.concat(calculator2Deps);
    }

    public resolveMask(pathWithMask: BindingPath): BindingPath[] {
        return this.performModelRead(model => MaskResolver.resolveMask(model, pathWithMask));
    }

    public openModalUnit(targetUnit: UnitId, modalUnit: UnitId, withScrollToUnit = true): void {
        runAsyncAction(async () => {
            await this.store.dispatch(async (innerDispatch, _, form) => {
                innerDispatch({
                    type: UnitActionType.ShowModalUnit,
                    payload: {
                        targetUnitId: targetUnit,
                        modalUnitId: modalUnit,
                    },
                });
                await delay(1);
                if (withScrollToUnit) {
                    await form.unitNavigator?.scrollToUnit(modalUnit);
                }
            });
        });
    }

    public closeModalUnit(targetUnit: UnitId): void {
        this.store.dispatch({
            type: UnitActionType.CloseModalUnit,
            payload: {
                targetUnitId: targetUnit,
            },
        });
    }

    private performModelUpdate(updateFn: (model: IModel) => Promise<void>): Promise<void>;
    private performModelUpdate(updateFn: (model: IModel) => void): void;
    private performModelUpdate(updateFn: (model: IModel) => void | Promise<void>): void | Promise<void> {
        const model = this.createModel();
        const eventCollectionContext = model.beginGetEvents();
        const eventsOrPromise = updateFn(model);
        if (eventsOrPromise == undefined) {
            const events = eventCollectionContext.releaseAndGetEvent();
            this.processEventsAfterUpdate(model, events);
            return Promise.resolve();
        } else {
            return (async () => {
                await eventsOrPromise;
                const events = eventCollectionContext.releaseAndGetEvent();
                this.processEventsAfterUpdate(model, events);
            })();
        }
    }

    private performModelRead<T>(readFn: (model: IFormReadonlyModelAccessor) => T): T {
        return readFn(this.modelOverStore);
    }

    private createModel(): IModel {
        return new Model(this.modelOverStore, this.modelNormalizer, this.useServerSideCalculations);
    }

    public async waitForPendingCalculations(
        calculationAwaitingOption: CalculationAwaitingOption = CalculationAwaitingOption.Calculations
    ): Promise<void> {
        await this.changingProcessor.waitForPendingCalculations(calculationAwaitingOption);
    }

    private processEventsAfterUpdate(sourceModel: IModel, updateEvents: readonly ModelEvent[]): void {
        this.changingProcessor.processEventsAfterUpdate(sourceModel, updateEvents);
    }

    private readonly __INTERNAL__getSectionFunction = (sectionName: string, nodePath: string): string[] => {
        const nodePathMask = this.dataDeclarationHelper.getMask(nodePath) ?? reject();

        const nodePathTokens = PathUtils.split(nodePath);
        const nodePathMaskTokens = PathUtils.split(nodePathMask);

        const instances: string[] = [];

        for (const [index, token] of IterUtils.iterateEntries(nodePathMaskTokens)) {
            if (token === "*") {
                const nodePathToken = nodePathTokens[index];
                if (nodePathToken != undefined) {
                    instances.push(nodePathToken);
                }
            }
        }

        return [SectionNames.defaultSection, sectionName].concat(
            instances.length > 0 ? [`${sectionName}-${instances.join("-")}`] : []
        );
    };

    private readonly __INTERNAL__getDefaultAutoFlag = (): void => {
        // Метод остался только, чтобы не падали approval тесты на Engine
    };

    public onErrorsCountChanged(listener: ModelEventsListener): void {
        if (this.errorCountChangeListeners.length === 0) {
            const formRootPath = getFormRootModelPath(this.settings);

            let prevErrorCount = currentViewModelSelect.pathViewModel(this.store.getState(), formRootPath).errorsCount;
            this.errorCountStateObserverUnsubscribe = this.store.subscribe(() => {
                const nextErrorCount = currentViewModelSelect.pathViewModel(
                    this.store.getState(),
                    formRootPath
                ).errorsCount;
                if (prevErrorCount != nextErrorCount) {
                    const errorCountChangeEvent: ModelEvent = {
                        value: nextErrorCount,
                        path: formRootPath,
                        name: "errorsCount",
                        oldValue: prevErrorCount,
                        type: "change",
                    };
                    for (const errorCountChangeListener of this.errorCountChangeListeners) {
                        errorCountChangeListener(errorCountChangeEvent);
                    }
                }
                prevErrorCount = nextErrorCount;
            });
        }
        this.errorCountChangeListeners.push(listener);
    }

    public offErrorsCountChanged(listener: ModelEventsListener): void {
        const listenerIndex = this.errorCountChangeListeners.findIndex(x => x === listener);
        if (listenerIndex >= 0) {
            this.errorCountChangeListeners.splice(listenerIndex, 1);
        }
        if (this.errorCountChangeListeners.length === 0) {
            this.errorCountStateObserverUnsubscribe?.();
            this.errorCountStateObserverUnsubscribe = undefined;
        }
    }

    public setHoveredPath(path: AbsoluteResolvedModelPath): void {
        if (!this.highlightedPath.isEquals(path)) {
            this.highlightedPath = path;
            const dependencyPaths = path.isEmpty() ? [] : this.getDependsOnPaths(path);
            this.dependencyPathsChangedSubscriptions.values().forEach(x => {
                x(dependencyPaths);
            });
        }
    }

    public setExternalHighlightedPaths(paths: HighlightedControlWithFocus[]): void {
        this.highlightedControlsChangedSubscriptions.values().forEach(x => {
            x(paths);
        });
    }

    public focusOnHighlightedControl(path: AbsoluteModelPath | undefined): void {
        const focusedPath: HighlightedControlWithFocus = {
            pathMask: path ? path : emptyAbsoluteModelPath(),
            focused: true,
        };
        this.highlightedControlsChangedSubscriptions.values().forEach(x => {
            x([focusedPath]);
        });
    }

    public onDependencyPathsFromHoveredPathChanged(callback: DependencyPathsCallback): () => void {
        const subscriptionId = uuid();
        this.dependencyPathsChangedSubscriptions.set(subscriptionId, callback);
        return () => {
            this.dependencyPathsChangedSubscriptions.delete(subscriptionId);
        };
    }

    public onExternalHighlightPaths(callback: HighlightedPathsCallback): () => void {
        const subscriptionId = uuid();
        this.highlightedControlsChangedSubscriptions.set(subscriptionId, callback);
        return () => {
            this.highlightedControlsChangedSubscriptions.delete(subscriptionId);
        };
    }

    public async focusFirstInvalidControl(): Promise<boolean> {
        const componentHandlesWithError = Iterator.from(iterateModelEntries(this.store.getState().viewModel))
            .filter(([_, viewModel]) => Boolean(viewModel.error?.[0]))
            .map(([path]) => this.componentHandleRegistry.getComponentHandles(path.toLegacyPath())?.[0])
            .filter(isNotNullOrUndefined);
        const mostTopAndLeftComponent = IterUtils.minByOrUndefined(componentHandlesWithError, (x, y) => {
            x.focus();
            return mostTopAndLeftRectComparer(x.getTargetBoundingClientRect(), y.getTargetBoundingClientRect());
        });
        setTimeout(() => {
            // конфликтует с фокусом в mostTopAndLeftComponent, поэтому в следующем такте
            mostTopAndLeftComponent?.focus();
        });
        return mostTopAndLeftComponent != undefined;
    }

    public focusFirstInvalidControlByPath(validationPath: string): boolean {
        const componentHandlesWithError = Iterator.from(iterateModelEntries(this.store.getState().viewModel))
            .filter(
                ([path, viewModel]) => path.toLegacyPath().startsWith(validationPath) && Boolean(viewModel.error?.[0])
            )
            .map(([path]) => this.componentHandleRegistry.getComponentHandles(path.toLegacyPath())?.[0])
            .filter(isNotNullOrUndefined);
        const mostTopAndLeftComponent = IterUtils.minByOrUndefined(componentHandlesWithError, (x, y) => {
            x.focus();
            return mostTopAndLeftRectComparer(x.getTargetBoundingClientRect(), y.getTargetBoundingClientRect());
        });
        mostTopAndLeftComponent?.focus();
        return mostTopAndLeftComponent != undefined;
    }

    public async importFromEmployeeService(
        options?: ImportFromEmployeeServiceOptions,
        initialState?: EmployeeListInitialState
    ): Promise<Employee[] | undefined> {
        const employeesDeferred = new Deferred<Employee[] | undefined>();
        this.store.dispatch(cancelImportDocuments());
        this.store.dispatch({
            type: ImportEmployeesActionType.BeginImport,
            payload: {
                deferredResult: employeesDeferred,
                initialState: initialState,
                options: options,
            },
        });
        return employeesDeferred.promise;
    }

    public importContractorsWidgetOptions(
        options: ImportContractorsWidgetOptions
    ): Promise<GetContractorResponse[] | undefined> {
        const contractorsDeferred = new Deferred<GetContractorResponse[] | undefined>();

        this.store.dispatch({
            type: ContractorsActionType.ImportViaSidePage,
            payload: {
                deferredResult: contractorsDeferred,
                options: options,
            },
        });
        return contractorsDeferred.promise;
    }

    public async importEmployerDocument(
        options: ImportEmployerDocumentsOptions
    ): Promise<EmployerNormalizedTag | undefined> {
        const documentDeferred = new Deferred<EmployerNormalizedTag | undefined>();
        this.store.dispatch(importDocuments(documentDeferred, options));
        return documentDeferred.promise;
    }

    public importEmployees(employees: Employee[], options?: ImportEmployeesOptions): Promise<void> {
        const employeeInfos = (employees || []).map(employee => ({
            employeeId: employee.id,
        }));
        const importRequest: ImportEmployeesRequest = {
            employerId: this.settings.employerId,
            employeeInfos: employeeInfos,
            employeesMultiplicityPath: options?.employeesMultiplicityPath,
            scenario: options?.scenario,
            documentsPeriod: options?.documentsPeriod,
        };
        return this.candyApiDraftClient.importEmployees(importRequest);
    }

    public runMassAction(actionId: string, parameters?: MassActionParameters): Promise<void> {
        return this.candyApiDraftClient.runMassAction(actionId, parameters);
    }

    private onConflictDetected() {
        if (this.settings.isReadOnly) {
            this.changeSet.stopAutosend();
            return;
        }
        runFloatingAsyncAction(async () => {
            if (this.isInConflictResolving) {
                return;
            }
            track("candy", CHANGE_SET_CONFLICT);
            const uiUtil = new UIUtil(this.settings.theme);
            this.isInConflictResolving = true;
            try {
                const reloadOrUpdateConfirmation = await uiUtil.confirm(
                    ConflictConfirmationText(),
                    "Обновить",
                    "Закрыть",
                    "Введенные данные не сохранятся"
                );
                if (reloadOrUpdateConfirmation) {
                    window.location.reload();
                }
            } finally {
                this.isInConflictResolving = false;
            }
        });
    }

    private async showOutdatedResourceAlertAndReload() {
        const uiUtil = new UIUtil(this.settings.theme);
        await uiUtil.confirm(ResourcesOutdatedText(), "Обновить", undefined, "Введенные данные не сохранятся");
        window.location.reload();
    }

    public async customNormalize(
        normalizerId: string | null,
        pathPrefixes: AbsoluteModelPath[],
        forceReload: boolean = false
    ): Promise<void> {
        await this.waitForPendingCalculations(CalculationAwaitingOption.CalculationsAndValidations);
        await this.changeSet.send();
        await this.changingProcessor.customNormalize(normalizerId, pathPrefixes);
        if (!this.useServerSideCalculations || forceReload) {
            await this.reloadInner();
        }
    }

    public async normalizeDirtyMasksAndReloadInnerIfRequired(): Promise<void> {
        await this.waitForPendingCalculations(CalculationAwaitingOption.CalculationsAndValidations);
        await this.changeSet.send();
        const dirtyChangedPathMasks = this.getViewModel(getFormRootModelPath(this.settings))?.dirtyChangedPathMasks;

        if (dirtyChangedPathMasks != undefined) {
            const uiUtil = new UIUtil(this.settings.theme);
            await uiUtil.executeLongOperation(
                {
                    allowCancel: false,
                    title: "Выполняется обработка черновика..",
                    modalSpecialCase: "InfiniteProcess",
                },
                async () => {
                    const changedPaths = await this.candyApiDraftClient.normalizeDirtyMasks();
                    if (changedPaths != null) {
                        await this.reloadInner();
                        await delay(100);
                        await this.lazyLoadingManager.waitForAllPendingRequests();
                    }
                }
            );
        }
    }

    public async setMultipleWithNormalization(rules: ValueForInstancesSettingRule[]): Promise<void> {
        await this.waitForPendingCalculations(CalculationAwaitingOption.CalculationsAndValidations);
        await this.changeSet.send();
        if (this.useServerSideCalculations || !this.lazyLoadingManager.isLazyLoadingEnabled()) {
            await this.changeSet.scheduleInnerChangeAction(() =>
                this.candyApiDraftClient.setMultipleWithNormalization(rules)
            );
        } else {
            await this.candyApiDraftClient.setMultipleWithNormalization(rules);
            await this.reloadInner();
        }
    }

    public getAttachmentsPublicFormsById(id: string): IPublicForm {
        const attachmentForm: IPublicForm | undefined = this.attachmentsPublicForms[id];

        if (attachmentForm === undefined) {
            throw new Error(`attachmentForm with id ${id} not found`);
        }
        return attachmentForm;
    }

    public getAttachmentPath(id: string): string | undefined {
        const state = this.store.getState();

        return state.attachments.byId[id]?.meta?.bindingPath;
    }

    public getPortalUser() {
        return this.candyApiDraftClient.getUser();
    }
}

function isResourcesAreSame(data: IReadonlyModelHunk, settings: FormSettings, resourcesHash: string): boolean {
    const oldResourcesHash = ModelHunk.get(data, settings.formRoot)?.resourcesHash ?? "";
    const isResourcesSame = resourcesHash === oldResourcesHash;
    return isResourcesSame;
}

const mostTopAndLeftRectComparer: CompareFunc<DOMRectReadOnly | undefined> = ComparerDecorators.undefinedOrNullAsMax(
    (left: DOMRectReadOnly, right: DOMRectReadOnly): CompareResult => {
        const result = compareNumbers(left.y, right.y);
        if (result === 0) {
            return compareNumbers(left.x, right.x);
        }
        return result;
    }
);

export function inferCabinetApiUrlFromKeformsUrl(keformsUrl: string): string {
    if (keformsUrl.startsWith("https://api-") || keformsUrl.startsWith("https://localhost")) {
        return "https://api.testkontur.ru/cabinet-api";
    }
    return keformsUrl.replace("/candy", "/cabinet-api");
}

function inferStaffWidgetUrlFromKeformsUrl(keformsUrl: string): string {
    if (keformsUrl.includes("forms20")) {
        return "https://ss-prerelease.staff.testkontur.ru/staff-widgets";
    }

    return keformsUrl.includes(".testkontur.ru")
        ? "https://api.testkontur.ru/staff-widgets"
        : "https://api.kontur.ru/staff-widgets";
}

function inferContractorsWidgetUrlFromKeformsUrl(keformsUrl: string): string {
    return keformsUrl.includes(".testkontur.ru")
        ? "https://api.testkontur.ru/staff-widgets"
        : "https://api.kontur.ru/staff-widgets";
}
